import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  insurers,
  parametrization,
  products,
  templateBindings,
  templates,
  type TemplateBinding,
  type TemplateBindingDocumentKind,
  type TemplateBindingMovement,
  type TemplateBindingProofStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

/**
 * Aba "Vinculos" de /templates (P-MIN-2): qual modelo de apolice gera o documento da
 * apolice, por seguradora, produto, grupo de modalidade ou modalidade e movimento, e
 * a prova de cada modelo. Ver docs/ADR/0057-documento-da-apolice.md.
 *
 * Campo vazio vale para todos; na emissao vale o vinculo ativo mais especifico. O
 * grupo de credito travado (o judicial sempre) so emite com a prova aprovada da versao
 * em uso: a prova e o documento gerado com os valores de exemplo, conferido por quem
 * tem a alcada.
 */

export const PERMISSAO_VER_VINCULOS = 'templates.vinculo.view';
const PERMISSAO_VINCULAR = 'templates.vinculo.update';
const PERMISSAO_APROVAR_PROVA = 'templates.vinculo.approve';
const PERMISSAO_CONFIGURAR = 'apolices.configuracao.update';

const movimentos: { value: TemplateBindingMovement; label: string }[] = [
  { value: 'emissao', label: 'Emissao' },
  { value: 'endosso', label: 'Endosso' },
  { value: 'cancelamento', label: 'Cancelamento' },
  { value: 'renovacao', label: 'Renovacao' },
];

// Uso do vinculo (P-MIN-5): as minutas da seguradora valem como minuta da cotacao,
// antes da emissao, e como documento da apolice.
const usos: { value: TemplateBindingDocumentKind; label: string }[] = [
  { value: 'apolice', label: 'Documento da apolice' },
  { value: 'minuta', label: 'Minuta da cotacao' },
];

const rotuloDaProva: Record<TemplateBindingProofStatus, string> = {
  pendente: 'Prova pendente',
  gerada: 'Prova gerada, aguardando conferencia',
  aprovada: 'Prova aprovada',
  reprovada: 'Prova reprovada',
  falhou: 'Prova falhou',
};

const tomDaProva: Record<
  TemplateBindingProofStatus,
  'success' | 'warning' | 'danger' | 'neutral' | 'info'
> = {
  pendente: 'neutral',
  gerada: 'info',
  aprovada: 'success',
  reprovada: 'danger',
  falhou: 'danger',
};

const rotuloDoGrupo: Record<string, string> = {
  tradicional: 'Tradicional',
  estruturadas: 'Estruturadas',
  judicial: 'Judicial',
  recursal: 'Recursal',
  financeira: 'Financeira',
  fianca: 'Fianca',
};

export function TemplateBindingsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [decidindo, setDecidindo] = useState<{
    vinculo: TemplateBinding;
    acao: 'aprovar' | 'reprovar';
  } | null>(null);

  const podeVincular = can(PERMISSAO_VINCULAR);
  const podeAprovar = can(PERMISSAO_APROVAR_PROVA);

  const vinculos = useQuery({
    queryKey: ['template-bindings'],
    queryFn: ({ signal }) => templateBindings.list({}, signal),
  });
  const seguradoras = useQuery({
    queryKey: ['insurers', { limit: 100 }],
    queryFn: ({ signal }) => insurers.list({ limit: 100 }, signal),
    retry: false,
  });
  const produtos = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
    retry: false,
  });
  const grupos = useQuery({
    queryKey: ['modality-groups', 'todos'],
    queryFn: ({ signal }) => parametrization.listGroups({}, signal),
    retry: false,
  });

  const nomeDaSeguradora = (id: string | null) =>
    id
      ? (seguradoras.data?.items.find((item) => item.id === id)?.legal_name ??
        'Seguradora')
      : null;
  const nomeDoProduto = (id: string | null) =>
    id ? (produtos.data?.find((item) => item.id === id)?.name ?? 'Produto') : null;
  const nomeDaModalidade = (id: string | null) => {
    if (!id) return null;
    for (const produto of produtos.data ?? []) {
      const achada = produto.modalities.find((item) => item.id === id);
      if (achada) return achada.name;
    }
    return 'Modalidade';
  };
  const nomeDoGrupo = (id: string | null) =>
    id ? (grupos.data?.find((item) => item.id === id)?.name ?? 'Grupo') : null;

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['template-bindings'] });
  }

  const prova = useMutation({
    mutationFn: ({
      id,
      acao,
      notas,
    }: {
      id: string;
      acao: 'gerar' | 'aprovar' | 'reprovar';
      notas?: string;
    }) => templateBindings.proof(id, acao, notas ?? ''),
    onSuccess: (vinculo) => {
      if (vinculo.proof.status === 'falhou') {
        toast.error(`A prova falhou: ${vinculo.proof.failure}`);
      } else {
        toast.success(rotuloDaProva[vinculo.proof.status]);
      }
      setDecidindo(null);
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const situacao = useMutation({
    mutationFn: (vinculo: TemplateBinding) =>
      templateBindings.update(vinculo.id, {
        status: vinculo.status === 'ativo' ? 'inativo' : 'ativo',
      }),
    onSuccess: (vinculo) => {
      toast.success(
        vinculo.status === 'ativo' ? 'Vinculo ativado.' : 'Vinculo inativado.',
      );
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const colunas: Column<TemplateBinding>[] = [
    {
      key: 'modelo',
      header: 'Modelo',
      render: (vinculo) => (
        <div className="min-w-0">
          <p className="font-medium text-ink-900">{vinculo.template_name}</p>
          <p className="text-2xs text-ink-500">
            {vinculo.current_version > 0
              ? `Versao em uso ${vinculo.current_version}`
              : 'Sem versao publicada'}
          </p>
        </div>
      ),
    },
    {
      key: 'escopo',
      header: 'Vale para',
      render: (vinculo) => {
        const partes = [
          nomeDaSeguradora(vinculo.insurer_id),
          nomeDoProduto(vinculo.product_id),
          nomeDoGrupo(vinculo.modality_group_id),
          nomeDaModalidade(vinculo.modality_id),
        ].filter((parte): parte is string => parte !== null);
        return partes.length > 0 ? partes.join(' / ') : 'Todas as apolices';
      },
    },
    {
      key: 'uso',
      header: 'Uso',
      hideBelow: 'md',
      render: (vinculo) =>
        usos.find((item) => item.value === (vinculo.document_kind ?? 'apolice'))
          ?.label ?? vinculo.document_kind,
    },
    {
      key: 'movimento',
      header: 'Movimento',
      hideBelow: 'md',
      render: (vinculo) =>
        movimentos.find((item) => item.value === vinculo.movement)?.label ??
        vinculo.movement,
    },
    {
      key: 'prova',
      header: 'Prova do modelo',
      render: (vinculo) => (
        <div className="flex flex-col gap-1">
          <Badge tone={tomDaProva[vinculo.proof.status]}>
            {rotuloDaProva[vinculo.proof.status]}
          </Badge>
          {vinculo.proof.outdated ? (
            <span className="text-2xs text-warning-700">
              Feita com a versao {vinculo.proof.template_version}: gere de novo para a
              versao em uso.
            </span>
          ) : null}
          {vinculo.proof.decided_at ? (
            <span className="text-2xs text-ink-500">
              {vinculo.proof.decided_label} em{' '}
              {formatDateTime(vinculo.proof.decided_at)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (vinculo) => (
        <Badge tone={vinculo.status === 'ativo' ? 'success' : 'neutral'}>
          {vinculo.status === 'ativo' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (vinculo) => (
        <div className="flex flex-wrap justify-end gap-1">
          {vinculo.proof.url ? (
            <AcaoComIcone
              acao="baixar"
              rotulo="Baixar prova"
              onClick={() =>
                window.open(
                  enderecoDeArquivo(vinculo.proof.url ?? ''),
                  '_blank',
                  'noopener,noreferrer',
                )
              }
            />
          ) : null}
          {podeVincular && vinculo.status === 'ativo' ? (
            // Gerar a prova produz o documento de conferencia: a folha, na cor de
            // imprimir e baixar.
            <AcaoComIcone
              icone="file"
              tom="indigo"
              rotulo="Gerar prova"
              loading={prova.isPending}
              onClick={() => prova.mutate({ id: vinculo.id, acao: 'gerar' })}
            />
          ) : null}
          {podeAprovar &&
          vinculo.proof.status === 'gerada' &&
          !vinculo.proof.outdated ? (
            <>
              <AcaoComIcone
                acao="aprovar"
                rotulo="Aprovar prova"
                onClick={() => setDecidindo({ vinculo, acao: 'aprovar' })}
              />
              <AcaoComIcone
                acao="recusar"
                rotulo="Reprovar prova"
                onClick={() => setDecidindo({ vinculo, acao: 'reprovar' })}
              />
            </>
          ) : null}
          {podeVincular ? (
            <AcaoComIcone
              acao={vinculo.status === 'ativo' ? 'desativar' : 'ativar'}
              rotulo={vinculo.status === 'ativo' ? 'Inativar' : 'Ativar'}
              loading={situacao.isPending}
              onClick={() => situacao.mutate(vinculo)}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <Alert tone="info" title="Como o documento da apolice e escolhido">
        Na emissao vale o vinculo ativo mais especifico: a seguradora pesa mais, depois
        a modalidade, o grupo e o produto. Sem vinculo, a apolice e emitida e o
        documento fica nao gerado, com o motivo. O grupo judicial so emite com o modelo
        vinculado e a prova aprovada da versao em uso. O uso Minuta da cotacao diz qual
        minuta a cotacao imprime antes da emissao.
      </Alert>

      {vinculos.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os vinculos">
          {vinculos.error.message}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Vinculos do documento da apolice"
          action={
            podeVincular ? (
              <Button onClick={() => setCriando(true)}>Novo vinculo</Button>
            ) : null
          }
        />
        <DataTable
          columns={colunas}
          items={vinculos.data ?? []}
          keyOf={(vinculo) => vinculo.id}
          loading={vinculos.isPending}
          empty={
            <EmptyState
              icon="copy"
              title="Nenhum modelo vinculado"
              description="Sem vinculo, a apolice e emitida sem documento gerado. Publique um modelo do tipo Apolice em Word e vincule aqui."
            />
          }
        />
      </Card>

      <PolicyDocumentSettingsCard />

      {criando ? (
        <BindingDialog
          seguradoras={(seguradoras.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.legal_name,
          }))}
          produtos={produtos.data ?? []}
          grupos={grupos.data ?? []}
          onClose={() => setCriando(false)}
          onSaved={() => {
            setCriando(false);
            invalidar();
          }}
        />
      ) : null}

      {decidindo ? (
        <ProofDecisionDialog
          acao={decidindo.acao}
          vinculo={decidindo.vinculo}
          submitting={prova.isPending}
          onClose={() => setDecidindo(null)}
          onConfirm={(notas) =>
            prova.mutate({ id: decidindo.vinculo.id, acao: decidindo.acao, notas })
          }
        />
      ) : null}
    </div>
  );
}

function BindingDialog({
  seguradoras,
  produtos,
  grupos,
  onClose,
  onSaved,
}: {
  seguradoras: { value: string; label: string }[];
  produtos: { id: string; name: string; modalities: { id: string; name: string }[] }[];
  grupos: { id: string; name: string; product_id: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [modelo, setModelo] = useState('');
  const [seguradora, setSeguradora] = useState('');
  const [produto, setProduto] = useState('');
  const [grupo, setGrupo] = useState('');
  const [modalidade, setModalidade] = useState('');
  const [movimento, setMovimento] = useState<TemplateBindingMovement>('emissao');
  const [notas, setNotas] = useState('');
  const [uso, setUso] = useState<TemplateBindingDocumentKind>('apolice');
  const daMinuta = uso === 'minuta';

  // O tipo do modelo segue o uso: a minuta da cotacao sai de modelo do tipo minuta.
  const modelos = useQuery({
    queryKey: ['templates', { kind: uso }],
    queryFn: ({ signal }) => templates.list({ kind: uso, limit: 100 }, signal),
  });

  const criar = useMutation({
    mutationFn: () =>
      templateBindings.create({
        template_id: modelo,
        insurer_id: daMinuta ? null : seguradora || null,
        product_id: produto || null,
        modality_group_id: grupo || null,
        modality_id: modalidade || null,
        movement: daMinuta ? 'emissao' : movimento,
        notes: notas,
        document_kind: uso,
      }),
    onSuccess: () => {
      toast.success('Modelo vinculado. Gere a prova para conferir o documento.');
      onSaved();
    },
  });

  const opcoesDeModelo = (modelos.data?.items ?? [])
    .filter((item) => item.format === 'docx')
    .map((item) => ({
      value: item.id,
      label:
        item.current_version > 0
          ? `${item.name} (v${item.current_version})`
          : `${item.name} (sem versao publicada)`,
    }));
  const modalidades = produtos.find((item) => item.id === produto)?.modalities ?? [];
  const gruposDoProduto = grupos.filter((item) => item.product_id === produto);

  function submit(event: FormEvent) {
    event.preventDefault();
    criar.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Novo vinculo"
      description="Campo vazio vale para todos. Informe a modalidade ou o grupo, nao os dois."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            type="submit"
            form="form-novo-vinculo"
            loading={criar.isPending}
            disabled={modelo === ''}
          >
            Vincular modelo
          </Button>
        </>
      }
    >
      <form id="form-novo-vinculo" onSubmit={submit} className="flex flex-col gap-4">
        {criar.error ? (
          <Alert tone="danger" title="Nao foi possivel vincular">
            {criar.error.message}
          </Alert>
        ) : null}
        <Select
          label="Uso do modelo"
          value={uso}
          options={usos}
          hint="A minuta da cotacao vale antes da emissao, sem seguradora e so para a emissao."
          onChange={(event) => {
            setUso(event.target.value as TemplateBindingDocumentKind);
            setModelo('');
            setSeguradora('');
            setMovimento('emissao');
          }}
        />
        {daMinuta ? (
          <Select
            label="Modelo de minuta"
            required
            placeholder={modelos.isPending ? 'Carregando...' : 'Selecione o modelo'}
            value={modelo}
            options={opcoesDeModelo}
            onChange={(event) => setModelo(event.target.value)}
          />
        ) : (
          <Select
            label="Modelo de apolice"
            required
            placeholder={modelos.isPending ? 'Carregando...' : 'Selecione o modelo'}
            value={modelo}
            options={opcoesDeModelo}
            onChange={(event) => setModelo(event.target.value)}
          />
        )}
        {daMinuta ? null : (
          <Select
            label="Seguradora do vinculo"
            placeholder="Todas as seguradoras"
            value={seguradora}
            options={seguradoras}
            onChange={(event) => setSeguradora(event.target.value)}
          />
        )}
        <Select
          label="Produto do vinculo"
          placeholder="Todos os produtos"
          value={produto}
          options={produtos.map((item) => ({ value: item.id, label: item.name }))}
          onChange={(event) => {
            setProduto(event.target.value);
            setGrupo('');
            setModalidade('');
          }}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Grupo de modalidade"
            placeholder="Todos os grupos"
            value={grupo}
            disabled={produto === '' || modalidade !== ''}
            options={gruposDoProduto.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            onChange={(event) => setGrupo(event.target.value)}
          />
          <Select
            label="Modalidade do vinculo"
            placeholder="Todas as modalidades"
            value={modalidade}
            disabled={produto === '' || grupo !== ''}
            options={modalidades.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(event) => setModalidade(event.target.value)}
          />
        </div>
        <Select
          label="Movimento"
          value={movimento}
          disabled={daMinuta}
          options={movimentos}
          hint="So a emissao gera documento hoje. Endosso e cancelamento sao do pacote do endosso."
          onChange={(event) =>
            setMovimento(event.target.value as TemplateBindingMovement)
          }
        />
        <Textarea
          label="Observacoes do vinculo"
          value={notas}
          maxLength={2000}
          onChange={(event) => setNotas(event.target.value)}
        />
      </form>
    </Modal>
  );
}

function ProofDecisionDialog({
  acao,
  vinculo,
  submitting,
  onClose,
  onConfirm,
}: {
  acao: 'aprovar' | 'reprovar';
  vinculo: TemplateBinding;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (notas: string) => void;
}) {
  const [notas, setNotas] = useState('');
  const reprovar = acao === 'reprovar';
  return (
    <Modal
      open
      onClose={onClose}
      title={reprovar ? 'Reprovar prova' : 'Aprovar prova'}
      description={`${vinculo.template_name}, versao ${vinculo.proof.template_version ?? '-'}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            variant={reprovar ? 'danger' : 'primary'}
            loading={submitting}
            disabled={reprovar && notas.trim() === ''}
            onClick={() => onConfirm(notas)}
          >
            {reprovar ? 'Reprovar prova' : 'Aprovar prova'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-700">
          {reprovar
            ? 'Diga o que esta errado no documento de prova. O modelo continua sem prova aprovada.'
            : 'Aprove so depois de conferir o documento de prova. A prova aprovada libera a emissao do grupo travado com esta versao do modelo.'}
        </p>
        <Textarea
          label="Observacao da prova"
          required={reprovar}
          value={notas}
          maxLength={2000}
          onChange={(event) => setNotas(event.target.value)}
        />
      </div>
    </Modal>
  );
}

/** Configuracao do documento da apolice: os grupos travados e o codigo SUSEP. */
function PolicyDocumentSettingsCard() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeConfigurar = can(PERMISSAO_CONFIGURAR);
  const consulta = useQuery({
    queryKey: ['policy-document-settings'],
    queryFn: ({ signal }) => templateBindings.settings(signal),
  });
  const [grupos, setGrupos] = useState<string[] | null>(null);
  const [modo, setModo] = useState<'aviso' | 'recusar' | null>(null);

  const salvar = useMutation({
    mutationFn: () =>
      templateBindings.saveSettings({
        ...(grupos ? { locked_credit_groups: grupos } : {}),
        ...(modo ? { broker_susep_code_mode: modo } : {}),
      }),
    onSuccess: (dados) => {
      toast.success('Configuracao do documento da apolice gravada.');
      queryClient.setQueryData(['policy-document-settings'], dados);
      setGrupos(null);
      setModo(null);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (consulta.isError) return null;
  const travados = grupos ?? consulta.data?.locked_credit_groups ?? ['judicial'];
  const modoAtual = modo ?? consulta.data?.broker_susep_code_mode ?? 'aviso';

  return (
    <Card>
      <CardHeader
        title="Configuracao do documento da apolice"
        description="Os grupos de credito que so emitem com o modelo vinculado e a prova aprovada, e o codigo SUSEP do corretor na emissao."
        action={
          podeConfigurar ? (
            <Button
              size="sm"
              loading={salvar.isPending}
              disabled={grupos === null && modo === null}
              onClick={() => salvar.mutate()}
            >
              Salvar configuracao
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2" disabled={!podeConfigurar}>
          <legend className="text-xs font-semibold text-ink-700">
            Grupos travados
          </legend>
          <div className="flex flex-wrap gap-4">
            {(consulta.data?.credit_groups ?? Object.keys(rotuloDoGrupo)).map(
              (grupo) => (
                <label
                  key={grupo}
                  className="flex items-center gap-2 text-sm text-ink-700"
                >
                  <input
                    type="checkbox"
                    checked={travados.includes(grupo)}
                    disabled={grupo === 'judicial' || !podeConfigurar}
                    onChange={(event) =>
                      setGrupos(
                        event.target.checked
                          ? [...travados, grupo]
                          : travados.filter((item) => item !== grupo),
                      )
                    }
                    className="size-4 rounded border-line-strong text-brand-500"
                  />
                  {rotuloDoGrupo[grupo] ?? grupo}
                </label>
              ),
            )}
          </div>
          <p className="text-2xs text-ink-500">
            O judicial fica sempre travado ate o modelo ser validado (Ato Conjunto
            TST.CSJT.CGJT n 1/2019).
          </p>
        </fieldset>
        <Select
          label="Codigo SUSEP do corretor na emissao"
          value={modoAtual}
          disabled={!podeConfigurar}
          options={[
            { value: 'aviso', label: 'Avisar e emitir' },
            { value: 'recusar', label: 'Recusar a emissao sem o codigo' },
          ]}
          onChange={(event) => setModo(event.target.value as 'aviso' | 'recusar')}
        />
      </CardBody>
    </Card>
  );
}
